
import axios from 'axios';

const baseURL = process.env.REACT_APP_API_BASE_URL;

if (!baseURL) {
  console.error('API base URL is not configured!');
  throw new Error('API base URL is missing in environment variables');
}

const api = axios.create({
  baseURL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});

// Global reference to auth context
let authContext;

// Enhanced request interceptor
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    
    const enhancedError = {
      ...error,
      isNetworkError: !error.response, // Key check: if no response, it's a network error
      message: error.message || 'Network request failed'
    };
    return Promise.reject(enhancedError);
  }
);

let refreshTokenRequest = null;

const refreshToken = async () => {
  if (refreshTokenRequest) {
    return refreshTokenRequest;
  }

  refreshTokenRequest = new Promise(async (resolve, reject) => {
    try {
      const response = await axios.post(
        `${baseURL}/refresh-token`,
        {},
        { withCredentials: true }
      );
      
      const newToken = response.data.access_token;
      localStorage.setItem('access_token', newToken);
      api.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
      resolve(newToken);
    } catch (error) {
      const enhancedError = {
        ...error,
        isNetworkError: !error.response, // Key check here too for refresh
        message: error.message || 'Token refresh failed'
      };
      reject(enhancedError);
    } finally {
      refreshTokenRequest = null;
    }
  });

  return refreshTokenRequest;
};

// --- MODIFIED RESPONSE INTERCEPTOR ---
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    
    // Get auth context if available
    if (!authContext && window.authContext) {
      authContext = window.authContext;
    }

    if (!error.response) {
      const enhancedError = {
        ...error,
        isNetworkError: true, 
        message: 'Unable to connect to the server. Please check your network connection.',
        response: { 
          status: 0 
        }
      };
      
      return Promise.reject(enhancedError);
    }

    // Special handling for login errors (if it reached the server and got 401)
    if (originalRequest.url === '/sign-in' && error.response.status === 401) {
      const enhancedError = new Error(error.response.data?.message || 'Invalid email or password');
      enhancedError.isAuthError = true;
      enhancedError.response = error.response; // Keep the original response for detail
      return Promise.reject(enhancedError);
    }

    
    if (error.response.status !== 401 || originalRequest._retry) {
      return Promise.reject(error);
    }

    // Handle refresh token failure (if it explicitly failed on /refresh-token endpoint)
    if (originalRequest.url === '/refresh-token') {
      if (authContext) {
        authContext.handleLogout();
      } else {
        localStorage.removeItem('access_token');
        localStorage.removeItem('user');
        delete api.defaults.headers.common['Authorization'];
        window.location.href = '/login?session=expired';
      }
      // Reject with the original error as it represents the refresh token failure itself.
      return Promise.reject(error);
    }

    originalRequest._retry = true;
    
    try {
      const newToken = await refreshToken();
      originalRequest.headers.Authorization = `Bearer ${newToken}`;
      return api(originalRequest);
    } catch (refreshError) {
      console.error('Refresh token failed during retry:', refreshError);
      if (authContext) {
        authContext.handleLogout();
      } else {
        localStorage.removeItem('access_token');
        localStorage.removeItem('user');
        delete api.defaults.headers.common['Authorization'];
        window.location.href = '/login?session=expired';
      }
      return Promise.reject(refreshError);
    }
  }
);

export default api;